import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";
import { Text } from "#components/atoms/Typographies/Text";
import { Modal } from "#components/molecules/Modal/Modal";
import {
  settleOptimizationInvalidation,
  usePendingOptimizationInvalidation,
} from "#modules/optimizations/infra/optimizationInvalidationConfirmation";
import { ImpactedOptimizationList } from "#modules/optimizations/view/ImpactedOptimizationList";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import type { ImpactedOptimization, OptimizationInvalidationOperation } from "shared";

import type { TFunction } from "i18next";

type DeleteOperation = Extract<OptimizationInvalidationOperation, { type: "DELETE" }>;

export const OptimizationInvalidationModal = () => {
  const pending = usePendingOptimizationInvalidation();
  const optimizations = pending?.optimizations ?? [];
  const operation = pending?.operation;

  if (operation?.type === "DELETE") {
    return <DeletionModal operation={operation} optimizations={optimizations} />;
  }
  return <UpdateModal opened={pending !== null} optimizations={optimizations} />;
};

const UpdateModal = ({
  opened,
  optimizations,
}: {
  opened: boolean;
  optimizations: ImpactedOptimization[];
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      opened={opened}
      variant="warning"
      icon={<Icon name="fillDanger" size={32} />}
      title={t("optimizations.invalidation.title", { count: optimizations.length })}
      primaryText={t("optimizations.invalidation.confirm")}
      closeText={t("optimizations.invalidation.back")}
      onClose={() => settleOptimizationInvalidation(false)}
      onPrimary={() => settleOptimizationInvalidation(true)}
    >
      <Text size="small" color={colors.text.secondaryDefault} textAlign="center">
        {t("optimizations.invalidation.subtitle")}
      </Text>
      <Flex w="100%" pt={spacing[2]}>
        <ImpactedOptimizationList optimizations={optimizations} />
      </Flex>
    </Modal>
  );
};

const DeletionModal = ({
  operation,
  optimizations,
}: {
  operation: DeleteOperation;
  optimizations: ImpactedOptimization[];
}) => {
  const { t } = useTranslation();
  const { title, body } = getDeletionTexts(operation, t);

  return (
    <Modal
      opened
      title={title}
      primaryText={t("quoteRequestV2.deleteLine.confirm")}
      primaryVariant="destructive"
      closeText={t("common.cancel")}
      onClose={() => settleOptimizationInvalidation(false)}
      onPrimary={() => settleOptimizationInvalidation(true)}
    >
      <Text size="small" color={colors.text.secondaryDefault}>
        {body}
      </Text>
      <Flex pt={spacing[3]}>
        <ImpactedOptimizationList optimizations={optimizations} withWarningHeader />
      </Flex>
    </Modal>
  );
};

const getDeletionTexts = (operation: DeleteOperation, t: TFunction) => {
  if (operation.resource === "QUOTE_REQUEST_LINE") {
    return {
      title: t("quoteRequestV2.deleteLine.title", { count: operation.count }),
      body: t("quoteRequestV2.deleteLine.body"),
    };
  }
  return {
    title: t("optimizations.invalidation.deleteProcessStep.title"),
    body: t("optimizations.invalidation.deleteProcessStep.body"),
  };
};
